import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/useAuth';
import { Button } from '@components/ui/Button/Button';
import { MfaEnrollment } from '@components/Mfa/MfaEnrollment/MfaEnrollment';
import {
  getMfaMethods,
  MfaPasswordRejectedError,
  MfaRateLimitedError,
  sendMfaEmailOtp,
  setPrimaryOtpChannel,
  regenerateBackupCodes,
  unenrollMfaMethod,
  verifyMfaEmail,
} from '@apis/mfa';
import { isMfaOtpChannel } from '@interfaces/IMfa';
import type { IMfaMethodsState, MfaEnrollMethod, MfaOtpChannel } from '@interfaces/IMfa';
import { MFA_CODE_LENGTH, MFA_OTP_RESEND_COOLDOWN_SECONDS } from '@constants/mfa';
import { useResendCooldown } from '@hooks/useResendCooldown';
import { BackupCodesIcon, EnrolledCheckIcon, MfaMethodIcon } from './mfaMethodIcons';
import { BackupCodesPanel, PasswordGateForm, PrimaryChannelRow } from './mfaPanels';
import { EmailCodeStep, EmailPasswordStep } from './EmailEnrollmentSteps';
import { SmsEnrollment } from './SmsEnrollment';
import { SsoManagedNotice } from './SsoManagedNotice';
import styles from './styles.module.css';

/** The factors with a real management flow today; everything else in `allowed`
    (sms / line) renders as a disabled "coming soon" affordance. */
const MANAGEABLE: readonly MfaEnrollMethod[] = ['totp', 'email', 'sms'];

/**
 * The active sub-flow within the two-factor card. The list is the resting state;
 * the others take over the card body while a single add is in progress.
 *  - `enroll-totp`     — the authenticator wizard (`MfaEnrollment`, settings).
 *  - `email-password`  — the password gate shown only when email is the *first*
 *                        factor (2FA off). Submitting it mails the ownership code
 *                        and advances to `email-code`; the password is held for
 *                        the verify call that follows.
 *  - `email-code`      — enter the 6-digit code just emailed. `withPassword`
 *                        carries the held password through to `verifyMfaEmail`
 *                        (true only for the first-factor path).
 *  - `email-codes`     — the one-time backup codes, shown once behind a save
 *                        gate (first-factor enrolment only).
 *  - `regen-password`  — the password gate for regenerating backup codes.
 *  - `regen-codes`     — the freshly minted codes, shown once (the OLD set is
 *                        already dead by the time this renders).
 *  - `remove-password` — the password gate for removing a factor; removing the
 *                        last one turns two-step verification off.
 */
type View =
  | { kind: 'list' }
  | { kind: 'enroll-totp' }
  | { kind: 'email-password' }
  | { kind: 'email-code'; withPassword: boolean }
  | { kind: 'email-codes'; codes: string[] }
  | { kind: 'enroll-sms' }
  | { kind: 'regen-password' }
  | { kind: 'regen-codes'; codes: string[] }
  | { kind: 'remove-password'; method: MfaEnrollMethod };

/**
 * Account security settings — the two-factor section, reached from the user
 * menu. A per-method manager over the account's factor set (`GET
 * /api/auth/mfa/methods`): each `allowed` method is a row that can be added or
 * removed independently. There is no global on/off switch — 2FA is "on" exactly
 * when at least one factor is enrolled, and the last removal turns it off.
 *
 * Enrolling email proves ownership of the address first: a code is mailed
 * (`sendMfaEmailOtp`) and must be echoed back (`verifyMfaEmail`) before the
 * factor turns on. When email is the *first* factor the account password gates
 * the enrolment too, and the backend answers with one-time backup codes.
 *
 * `setMfaEnabled` keeps the rest of the session (top bar, menu) honest: patched
 * true after any enrol, false once the final factor is removed.
 */
export function SecurityPage() {
  const { user } = useAuth();
  // A company-account session has no d-orca password or MFA to manage.
  return user?.authMethod === 'sso' ? <SsoManagedNotice /> : <MfaManager />;
}

function MfaManager() {
  const { t } = useTranslation();
  const { setMfaEnabled } = useAuth();

  const [methods, setMethods] = useState<IMfaMethodsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [view, setView] = useState<View>({ kind: 'list' });
  const [settingChannel, setSettingChannel] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busyMethod, setBusyMethod] = useState<MfaEnrollMethod | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const { cooldown, startCooldown, clearCooldown } = useResendCooldown();
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  /** Re-read the factor set and mirror the on/off state into the auth context. */
  const refresh = useCallback(async (): Promise<IMfaMethodsState> => {
    const next = await getMfaMethods();
    setMethods(next);
    setMfaEnabled(next.enrolled.length > 0);
    return next;
  }, [setMfaEnabled]);

  useEffect(() => {
    let alive = true;
    getMfaMethods()
      .then((next) => {
        if (!alive) return;
        setMethods(next);
        setMfaEnabled(next.enrolled.length > 0);
      })
      .catch(() => {
        if (alive) setLoadError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [setMfaEnabled]);

  // The engine's own flag, NOT the set size — they diverge for a user who predates the method
  // set, and inferring "off" there makes the email flow run `enable`, rotating their secret.
  const enabled = methods?.twoFactorEnabled ?? false;

  function toList() {
    setView({ kind: 'list' });
    setPassword('');
    setCode('');
    setErr(null);
    clearCooldown();
  }

  /** Mail (or re-mail) the email-OTP, arming the resend cooldown. */
  async function sendCode(): Promise<boolean> {
    if (sendingOtp || cooldown > 0) return false;
    setSendingOtp(true);
    setErr(null);
    try {
      await sendMfaEmailOtp();
      startCooldown(MFA_OTP_RESEND_COOLDOWN_SECONDS);
      return true;
    } catch (error) {
      // A 401 here is `MfaInvalidCodeError` (dead session), a 429 is
      // `MfaRateLimitedError` — neither reveals anything about a code, so a
      // single neutral "couldn't send" covers both, except the rate-limit note.
      setErr(
        error instanceof MfaRateLimitedError
          ? t('auth.mfa.rateLimited')
          : t('auth.mfa.settings.emailSendError'),
      );
      return false;
    } finally {
      setSendingOtp(false);
    }
  }

  /** Mint a fresh recovery set. The OLD codes die the moment this succeeds. */
  async function onSubmitRegenPassword(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const codes = await regenerateBackupCodes(password);
      setPassword('');
      setView({ kind: 'regen-codes', codes });
    } catch (error) {
      setErr(
        error instanceof MfaPasswordRejectedError
          ? t('auth.mfa.settings.wrongPassword')
          : error instanceof MfaRateLimitedError
            ? t('auth.mfa.rateLimited')
            : t('auth.mfa.settings.updateError'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function onRegenerate() {
    setFlash(null);
    setErr(null);
    setPassword('');
    setView({ kind: 'regen-password' });
  }

  /**
   * Change which channel a future challenge goes to by default.
   *
   * Refreshed from the server rather than patched locally: the engine promotes a survivor when
   * the principal is unenrolled, so the local guess and the stored value can already disagree.
   */
  async function onSetPrimaryChannel(channel: MfaOtpChannel) {
    if (settingChannel) return;
    setErr(null);
    setFlash(null);
    setSettingChannel(true);
    try {
      await setPrimaryOtpChannel(channel);
      setFlash(t('auth.mfa.settings.primaryChannelFlash'));
      await refresh();
    } catch {
      setErr(t('auth.mfa.settings.primaryChannelError'));
    } finally {
      setSettingChannel(false);
    }
  }

  function onAdd(method: MfaEnrollMethod) {
    setFlash(null);
    setErr(null);
    if (method === 'totp') {
      setView({ kind: 'enroll-totp' });
      return;
    }
    // SMS owns its own flow (password → number → code) in a sibling component: it has a step
    // email does not — the number, which the user types and the backend proves before storing.
    if (method === 'sms') {
      setView({ kind: 'enroll-sms' });
      return;
    }
    // Email: the password gate exists only for the first factor. Either way the
    // address is proven by an emailed code before the factor turns on.
    if (!enabled) {
      setPassword('');
      setCode('');
      setView({ kind: 'email-password' });
    } else {
      void beginEmailCode();
    }
  }

  /** Already-on path: no password — mail the code and open the code step. */
  async function beginEmailCode() {
    setBusyMethod('email');
    setCode('');
    const ok = await sendCode();
    setBusyMethod(null);
    if (ok) setView({ kind: 'email-code', withPassword: false });
  }

  /** First-factor path: capture the password, mail the code, open the code step. */
  async function onSubmitEmailPassword(e: FormEvent) {
    e.preventDefault();
    if (sendingOtp) return;
    setCode('');
    const ok = await sendCode();
    // Keep `password` in state — the verify call on the code step still needs it.
    if (ok) setView({ kind: 'email-code', withPassword: true });
  }

  async function onSubmitEmailCode(rawCode: string, withPassword: boolean) {
    const value = rawCode.trim();
    if (value.length !== MFA_CODE_LENGTH || submitting) return;
    setErr(null);
    setSubmitting(true);
    const wasOff = !enabled;
    try {
      const { backupCodes } = await verifyMfaEmail(value, withPassword ? password : undefined);
      setCode('');
      setPassword('');
      clearCooldown();

      // SHOW THE CODES BEFORE ANYTHING ELSE CAN THROW. The server keeps only hashes, so these
      // are the user's single sighting of their recovery codes — and two-step verification is
      // already ON by this point. Letting a later failure (the refresh below) reach the catch
      // would discard them for good and tell the user to "try again" on a step that already
      // succeeded.
      if (backupCodes && backupCodes.length > 0) {
        setView({ kind: 'email-codes', codes: backupCodes });
      } else {
        // No codes to save — drop straight back to the list.
        setFlash(t(wasOff ? 'auth.mfa.settings.enabledFlash' : 'auth.mfa.settings.updatedFlash'));
        toList();
      }

      // Refreshing the factor list is bookkeeping: it must never be able to undo the above.
      try {
        await refresh();
      } catch {
        // The enrolment stands; only the on-screen list is stale until the next load.
      }
    } catch (error) {
      // The backend answers one neutral 401 for a bad/expired/exhausted code AND
      // a rejected password — indistinguishable by design. So where a password
      // was in play (first factor) the copy names BOTH fields rather than
      // misattributing a wrong code to the password; otherwise it can only be the
      // code. The user stays on the code step either way.
      setErr(
        error instanceof MfaPasswordRejectedError
          ? withPassword
            ? t('auth.mfa.settings.emailCodeOrPassword')
            : t('auth.mfa.settings.emailInvalidCode')
          : error instanceof MfaRateLimitedError
            ? t('auth.mfa.rateLimited')
            : t('auth.mfa.settings.updateError'),
      );
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  /** Removing is password gated — it is the only way to turn two-step verification off. */
  function onRemove(method: MfaEnrollMethod) {
    setFlash(null);
    setErr(null);
    setPassword('');
    setView({ kind: 'remove-password', method });
  }

  async function onSubmitRemove(e: FormEvent, method: MfaEnrollMethod) {
    e.preventDefault();
    if (submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const { enrolled } = await unenrollMfaMethod(method, password);
      setPassword('');
      // Removal already happened upstream — the refresh is bookkeeping and must not be able to
      // report it as a failure (which would invite the user to retry an action that succeeded).
      try {
        await refresh();
      } catch {
        // The factor is gone; only the on-screen list is stale.
      }
      setFlash(t(enrolled.length === 0 ? 'auth.mfa.settings.disabledFlash' : 'auth.mfa.settings.updatedFlash'));
      toList();
    } catch (error) {
      setErr(
        error instanceof MfaPasswordRejectedError
          ? t('auth.mfa.settings.wrongPassword')
          : error instanceof MfaRateLimitedError
            ? t('auth.mfa.rateLimited')
            : t('auth.mfa.settings.updateError'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <section className={styles.card} aria-labelledby="mfa-section-title">
        <div className={styles.cardHeader}>
          <div>
            <h2 className={styles.cardTitle} id="mfa-section-title">
              {t('auth.mfa.settings.title')}
            </h2>
            <p className={styles.cardBody}>{t('auth.mfa.settings.body')}</p>
          </div>
          <span className={enabled ? `${styles.pill} ${styles.pillOn}` : styles.pill}>
            {t(enabled ? 'auth.mfa.settings.statusOn' : 'auth.mfa.settings.statusOff')}
          </span>
        </div>

        {flash ? (
          <div className={styles.flash} role="status">
            {flash}
          </div>
        ) : null}
        {err ? (
          <div className={styles.error} role="alert">
            {err}
          </div>
        ) : null}

        {loading ? (
          <p className={styles.loading}>{t('common.loading')}</p>
        ) : loadError || !methods ? (
          <p className={styles.loadError} role="alert">
            {t('auth.mfa.settings.loadError')}
          </p>
        ) : view.kind === 'list' ? (
          <ul className={styles.methodList}>
            {methods.allowed.map((method) => {
              const isEnrolled = methods.enrolled.includes(method);
              const manageable = MANAGEABLE.includes(method);
              const busy = busyMethod === method;
              return (
                <li className={styles.methodRow} key={method}>
                  <span className={isEnrolled ? `${styles.tile} ${styles.tileOn}` : styles.tile}>
                    <MfaMethodIcon method={method} />
                  </span>
                  <div className={styles.methodInfo}>
                    <span className={styles.methodLabel}>
                      {t(`auth.mfa.method.${method}`)}
                      {isEnrolled ? (
                        <span className={styles.badge}>
                          <EnrolledCheckIcon />
                          {t('auth.mfa.settings.enrolledBadge')}
                        </span>
                      ) : null}
                    </span>
                    <span className={styles.methodHint}>{t(`auth.mfa.settings.hint.${method}`)}</span>
                  </div>
                  {/* Enrolled always wins over "coming soon": a factor you somehow have must be
                      removable, even if this build cannot ADD it yet. */}
                  {!manageable && !isEnrolled ? (
                    <span className={styles.comingSoon}>{t('auth.mfa.settings.comingSoon')}</span>
                  ) : isEnrolled ? (
                    <Button variant="danger" size="sm" disabled={busy} onClick={() => onRemove(method)}>
                      {busy ? t('auth.mfa.settings.removing') : t('auth.mfa.settings.remove')}
                    </Button>
                  ) : (
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => onAdd(method)}>
                      {busy ? t('auth.mfa.settings.adding') : t('auth.mfa.settings.add')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}

        {view.kind === 'list' && !loading && !loadError && methods ? (
          <PrimaryChannelRow
            channels={methods.enrolled.filter(isMfaOtpChannel)}
            value={methods.primaryOtpChannel}
            busy={settingChannel}
            onChange={onSetPrimaryChannel}
          />
        ) : null}

        {/* Recovery gets its own home rather than being implied by the method list. */}
        {view.kind === 'list' && !loading && !loadError && methods && enabled ? (
          <div className={styles.recoveryRow}>
            <span className={styles.tile}>
              <BackupCodesIcon />
            </span>
            <div className={styles.methodInfo}>
              <span className={styles.methodLabel}>{t('auth.mfa.settings.backupTitle')}</span>
              <span className={styles.methodHint}>{t('auth.mfa.settings.backupHint')}</span>
            </div>
            <Button variant="secondary" size="sm" onClick={onRegenerate}>
              {t('auth.mfa.settings.regenerate')}
            </Button>
          </div>
        ) : null}

        {view.kind === 'enroll-totp' ? (
          <div className={styles.enrollSlot}>
            <MfaEnrollment
              context="settings"
              onConfirmed={async () => {
                const wasOff = !enabled;
                // The factor IS enrolled by now. `MfaEnrollment` awaits this inside its own
                // try/catch and renders `MfaInvalidCodeError` as "that code isn't valid" — and
                // `getMfaMethods` throws exactly that on a 401. So a stale-list refresh must
                // never be allowed to masquerade as a rejected code.
                try {
                  await refresh();
                } catch {
                  // Enrolment stands; only the on-screen list is stale.
                }
                setFlash(t(wasOff ? 'auth.mfa.settings.enabledFlash' : 'auth.mfa.settings.updatedFlash'));
                toList();
              }}
              onCancel={toList}
            />
          </div>
        ) : null}

        {view.kind === 'email-password' ? (
          <EmailPasswordStep
            password={password}
            onPasswordChange={setPassword}
            onSubmit={onSubmitEmailPassword}
            onCancel={toList}
            sending={sendingOtp}
          />
        ) : null}

        {view.kind === 'email-code' ? (
          <EmailCodeStep
            code={code}
            onCodeChange={setCode}
            onSubmit={(value) => void onSubmitEmailCode(value, view.kind === 'email-code' && view.withPassword)}
            onResend={() => void sendCode()}
            onCancel={toList}
            submitting={submitting}
            sending={sendingOtp}
            cooldown={cooldown}
          />
        ) : null}

        {view.kind === 'enroll-sms' ? (
          <SmsEnrollment
            twoFactorEnabled={enabled}
            onCancel={toList}
            onEnrolled={(backupCodes) => {
              const wasOff = !enabled;
              // Same ordering rule as the email path: the codes are the user's ONE sighting of
              // their recovery set, so show them before the bookkeeping refresh can throw.
              if (backupCodes && backupCodes.length > 0) {
                setView({ kind: 'email-codes', codes: backupCodes });
              } else {
                setFlash(t(wasOff ? 'auth.mfa.settings.enabledFlash' : 'auth.mfa.settings.updatedFlash'));
                toList();
              }
              setMfaEnabled(true);
              void refresh().catch(() => {
                // The enrolment stands; only the on-screen list is stale until the next load.
              });
            }}
          />
        ) : null}

        {view.kind === 'email-codes' ? (
          <BackupCodesPanel
            title={t('auth.mfa.enroll.backupTitle')}
            body={t('auth.mfa.enroll.backupBody')}
            codesLabel={t('auth.mfa.enroll.backupTitle')}
            codes={view.codes}
            onDone={() => {
              setFlash(t('auth.mfa.settings.enabledFlash'));
              toList();
            }}
          />
        ) : null}

        {view.kind === 'remove-password' ? (
          <PasswordGateForm
            title={t('auth.mfa.settings.removeTitle')}
            body={t('auth.mfa.settings.removeBody', { method: t(`auth.mfa.method.${view.method}`) })}
            submitLabel={t('auth.mfa.settings.remove')}
            submittingLabel={t('auth.mfa.settings.removing')}
            submitVariant="danger"
            password={password}
            onPasswordChange={setPassword}
            submitting={submitting}
            onCancel={toList}
            onSubmit={(e) => void onSubmitRemove(e, view.method)}
          />
        ) : null}

        {/* The warning in `regenBody` is the point of this step: the old codes stop working. */}
        {view.kind === 'regen-password' ? (
          <PasswordGateForm
            title={t('auth.mfa.settings.regenTitle')}
            body={t('auth.mfa.settings.regenBody')}
            submitLabel={t('auth.mfa.settings.regenerate')}
            submittingLabel={t('auth.mfa.settings.regenerating')}
            submitVariant="primary"
            password={password}
            onPasswordChange={setPassword}
            submitting={submitting}
            onCancel={toList}
            onSubmit={onSubmitRegenPassword}
          />
        ) : null}

        {view.kind === 'regen-codes' ? (
          <BackupCodesPanel
            title={t('auth.mfa.settings.regenDoneTitle')}
            body={t('auth.mfa.settings.regenDoneBody')}
            codesLabel={t('auth.mfa.settings.backupTitle')}
            codes={view.codes}
            onDone={() => {
              setFlash(t('auth.mfa.settings.regenFlash'));
              toList();
            }}
          />
        ) : null}

      </section>
    </div>
  );
}
